import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Target, Check, X, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

export default function Accuracy() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/accuracy").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-80 w-full rounded-2xl" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">No track record yet.</div>;

  const chart = [...data.records].reverse().map((r) => ({ date: r.date.slice(5), hit: r.hit_rate }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Past Picks — Track Record</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Each day we rank the watchlist, take the top 5, and grade them the next day. A pick is
          <strong className="text-foreground"> "correct" only if it beat the S&P 500</strong> — harder to game, more honest.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-6 col-span-2 md:col-span-1" data-testid="hit-rate-badge">
          <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-emerald-500 mb-2">
            <Target className="h-3.5 w-3.5" /> Rolling Hit Rate
          </div>
          <div className="text-4xl sm:text-5xl font-black font-mono text-emerald-500">{data.rolling_hit_rate}%</div>
          <p className="text-xs text-muted-foreground mt-1">of top picks beat the S&P 500</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Picks Graded</div>
          <div className="text-3xl font-bold font-mono">{data.total_evaluated}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Beat Index</div>
          <div className="text-3xl font-bold font-mono text-emerald-500">{data.total_beat}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">Daily Hit-Rate (%)</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <ReferenceLine y={50} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="hit" stroke="#10B981" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">
          Daily Picks & Outcomes
        </div>
        <div className="divide-y divide-border/60" data-testid="accuracy-records">
          {data.records.map((r) => (
            <div key={r.date} className="px-5 py-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold">{r.date}</span>
                  <span className="text-xs text-muted-foreground">S&P: {r.spy_return_pct >= 0 ? "+" : ""}{r.spy_return_pct}%</span>
                </div>
                <span className={cn("text-xs font-mono font-bold px-2 py-0.5 rounded-full border",
                  r.hit_rate >= 50 ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-amber-500 border-amber-500/30 bg-amber-500/10")}>
                  {r.beat_count}/{r.total} beat index
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.picks.map((p) => (
                  <span key={p.ticker}
                    className={cn("text-xs font-mono px-2 py-1 rounded-md border inline-flex items-center gap-1",
                      p.beat_sp ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-red-500 border-red-500/30 bg-red-500/10")}>
                    {p.beat_sp ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {p.ticker}
                    {p.return_pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {p.return_pct}%
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
